import BrandGroupDetailsPage, {
  BrandGroupFormData
} from "@saleor/brands/components/BrandGroupDetailsPage/BrandGroupDetailsPage";
import { useBrandGroupUpdate } from "@saleor/brands/mutation";
import { useBrandGroupsDetailsQuery } from "@saleor/brands/queries";
import { BrandGroupUpdate } from "@saleor/brands/types/brandGroupUpdate";
import { brandsListUrl, BrandsUrlQueryParams } from "@saleor/brands/url";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { commonMessages, sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import useBrandTagSearch from "@saleor/searches/useBrandTagSearch";
import { BrandGroupCreateInput } from "@saleor/types/globalTypes";
import React from "react";
import { useIntl } from "react-intl";

interface BrandGropupDetailsProps {
  id: string;
  params: BrandsUrlQueryParams;
}

export const BrandGroupDetails: React.FC<BrandGropupDetailsProps> = props => {
  const { id } = props;
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [iconToUpload, setIconToUpload] = React.useState(null);

  const { data, loading, refetch } = useBrandGroupsDetailsQuery({
    displayLoader: true,
    variables: {
      id
    }
  });

  const {
    loadMore: loadMoreTags,
    search: searchTags,
    result: searchTagsOpts
  } = useBrandTagSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const tags = maybe(() => searchTagsOpts.data.search.edges, []).map(
    edge => edge.node
  );

  const handlebrandGroupUpdate = (data: BrandGroupUpdate) => {
    if (data.brandGroupUpdate.brandErrors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
      refetch();
    } else if (
      data.brandGroupUpdate.brandErrors.some(e => e.code === "INVALID") &&
      data.brandGroupUpdate.brandErrors.some(e => e.field === "image")
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Wrong image dimension! Try resizing."
        })
      });
    } else {
      const brandImageError = data.brandGroupUpdate.brandErrors.find(
        error => error.field === ("image" as keyof BrandGroupCreateInput)
      );
      if (brandImageError) {
        notify({
          status: "error",
          text: data.brandGroupUpdate.brandErrors[0].message
        });
      }
    }
  };

  const [updateBrandGroup, updateBrandGroupresult] = useBrandGroupUpdate({
    onCompleted: handlebrandGroupUpdate
  });

  const handleIsSmacAffiliateToggle = async (isSmacAffiliate: boolean) => {
    const result = await updateBrandGroup({
      variables: {
        id,
        input: {
          isSmacAffiliate
        }
      }
    });
    return result.data.brandGroupUpdate.brandErrors;
  };

  const handleUpdate = async (formData: BrandGroupFormData) => {
    const result = await updateBrandGroup({
      variables: {
        id,
        input: {
          isSmacAffiliate: formData.isSmacAffiliate,
          ...(iconToUpload && { image: iconToUpload }),
          ...(formData.image === null && { image: iconToUpload }),
          tagsStr: formData?.tags?.map(tag => tag?.label) || []
        }
      }
    });
    return result.data.brandGroupUpdate.brandErrors;
  };

  const handleSubmit = (data?.brandGroup, handleUpdate);

  return (
    <>
      <WindowTitle title={intl.formatMessage(sectionNames.brands)} />
      <BrandGroupDetailsPage
        errors={
          updateBrandGroupresult?.data?.brandGroupUpdate.brandErrors || []
        }
        onBack={() => navigate(brandsListUrl())}
        disabled={loading}
        saveButtonBarState={updateBrandGroupresult.status}
        onDelete={undefined}
        onSubmit={handleSubmit}
        brandGroup={maybe(() => data.brandGroup)}
        onIconUpload={setIconToUpload}
        onFileSizeError={() =>
          notify({
            status: "error",
            text: intl.formatMessage({
              defaultMessage:
                "File size of the image you are trying to upload is too large."
            }),
            title: intl.formatMessage({
              defaultMessage: "Can't Upload."
            })
          })
        }
        onIsSmacAffiliate={handleIsSmacAffiliateToggle}
        tags={tags}
        fetchMoreTags={{
          hasMore: maybe(() => searchTagsOpts.data.search.pageInfo.hasNextPage),
          loading: searchTagsOpts.loading,
          onFetchMore: loadMoreTags
        }}
        fetchTags={searchTags}
      />
    </>
  );
};

export default BrandGroupDetails;
